<template>
  <div class="w-100">
    <div v-if="displayValues.length" class="d-flex flex-wrap row-gap-2 mb-4 col-gap-2">
      <e-chip
        v-for="(item, index) in displayValues"
        :key="index"
        closable
        @click:close="removeItem(item)"
      >
        {{ getItemTitle(item) }}
      </e-chip>
    </div>

    <e-autocomplete
      v-model="selectedValues"
      hide-selection
      show-menu-icon
      :items="flattenedOptions"
      :label="label"
      :rules="rules"
      multiple
      item-title="title"
      item-value="value"
      @update:search="onSearchUpdate"
    >
      <template #item="{ item, props }">
        <div 
          v-if="item.raw.isGroup" 
          :key="'group-' + item.raw.value" 
          class="group-header pl-4 text-body-2 py-3"
        >
          <div class="group-title">{{ item.raw.title }}:</div>
        </div>
        
        <v-list-item
          v-else
          :key="'item-' + item.raw.value"
          v-bind="props"
          @click="toggleItem(item.raw)"
        >
          <template #prepend>
            <v-checkbox-btn
              :model-value="isItemSelected(item.raw)"
              @click.stop="toggleItem(item.raw)"
            />
          </template>
        </v-list-item>
      </template>
    </e-autocomplete>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    T extends Record<string, unknown> | string,
    K extends T extends Record<string, unknown> ? Paths<T> | undefined : undefined
  "
>
import { computed, ref } from 'vue';
import type { InputRules, SelectionValue } from '@/core/types/components/components';
import type { Paths } from '@/core/types/gengeric';
import { EAutocomplete, EChip } from '@/core/components/ui';
import { getNestedObjectProperty } from '@/core/utils/object';
import { isString } from '@/core/utils/type-guards';

export interface Group<T = string> {
  groupName: string;
  items: T[];
}

interface ListItem {
  value: string;
  title: string;
  isGroup: boolean;
  [key: string]: unknown;
}

const props = withDefaults(
  defineProps<{
    groups?: Group<T>[]; 
    options?: T[];
    itemTitle?: string;
    itemValue?: K;
    rules?: InputRules;
    label?: string;
  }>(),
  {
    itemTitle: 'name',
  },
);

const searchQuery = ref(''); 

const modelValue = defineModel<SelectionValue<T, K>[]>({ required: true });

const displayValues = computed(() => modelValue.value || []);

const getItemTitle = (item: any): string => {
  if (!item) return '';
  if (isString(item)) return item;
  return getNestedObjectProperty(item, props.itemTitle) || '';
};

const flattenedOptions = computed<ListItem[]>(() => {
  if (props.groups?.length) {
    if (searchQuery.value) {
      const result: ListItem[] = [];
      const query = searchQuery.value.toLowerCase();
      
      props.groups.forEach((group, groupIndex) => {
        const matchingItems = group.items.filter(item => 
          getItemTitle(item).toLowerCase().includes(query)
        );
        
        if (matchingItems.length > 0) {
          result.push({
            value: `group-${groupIndex}-${group.groupName}`,
            title: group.groupName,
            isGroup: true
          });
          
          matchingItems.forEach((item, itemIndex) => {
            result.push({
              value: `item-${groupIndex}-${itemIndex}-${getItemTitle(item)}`,
              title: getItemTitle(item),
              isGroup: false
            });
          });
        }
      });
      
      return result;
    } else {
      const result: ListItem[] = [];
      
      props.groups.forEach((group, groupIndex) => {
        result.push({
          value: `group-${groupIndex}-${group.groupName}`,
          title: group.groupName,
          isGroup: true
        });
        
        group.items.forEach((item, itemIndex) => {
          result.push({
            value: `item-${groupIndex}-${itemIndex}-${getItemTitle(item)}`,
            title: getItemTitle(item),
            isGroup: false
          });
        });
      });
      
      return result;
    }
  }
  
  if (searchQuery.value && props.options?.length) {
    const query = searchQuery.value.toLowerCase();
    return (props.options || [])
      .filter(opt => getItemTitle(opt).toLowerCase().includes(query))
      .map((opt, index) => ({
        value: `item-${index}-${Date.now()}`,
        title: getItemTitle(opt),
        isGroup: false
      }));
  }
  
  return (props.options || []).map((opt, index) => ({
    value: `item-${index}-${Date.now()}`,
    title: getItemTitle(opt),
    isGroup: false
  }));
});


const selectedValues = computed({
  get: () => {
    return displayValues.value.map(item => {
      const title = getItemTitle(item);
      const found = flattenedOptions.value.find(opt => !opt.isGroup && opt.title === title);
      return found ? found.value : `temp-${title}`;
    });
  },
  set: (values) => {
    const selectedTitles = values
      .map(value => {
        const found = flattenedOptions.value.find(opt => opt.value === value);
        return found && !found.isGroup ? found.title : null;
      })
      .filter(Boolean);
    
    modelValue.value = displayValues.value.filter(item => {
      const title = getItemTitle(item);
      return selectedTitles.includes(title);
    });
  },
});

const removeItem = (item: any) => {
  const titleToRemove = getItemTitle(item);
  modelValue.value = displayValues.value.filter(i => getItemTitle(i) !== titleToRemove);
};

const isItemSelected = (item: ListItem) => {
  return displayValues.value.some(i => getItemTitle(i) === item.title);
};

const toggleItem = (item: ListItem) => {
  if (item.isGroup) return;
  
  const currentTitles = displayValues.value.map(i => getItemTitle(i));
  
  if (currentTitles.includes(item.title)) {
    modelValue.value = displayValues.value.filter(i => getItemTitle(i) !== item.title);
  } else {
    const newItem = isString(item.title) ? item.title : { [props.itemTitle]: item.title };
    modelValue.value = [...displayValues.value, newItem as any];
  }
};

const onSearchUpdate = (query: string) => {
  searchQuery.value = query;
};
</script>

<style scoped lang="scss">
.group-header {
  font-weight: 600;
}
</style>